import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { ActivityFilter, ListResponse } from '@shared/schemas/pagination';
import type {
  CreateRecurringRuleInput,
  GenerationResult,
  RecurringRuleView,
  UpdateRecurringRuleInput,
} from '@shared/schemas/recurring';

/* The queries behind /admin/recurring-jobs. ROADMAP.md §7.5. */

const ROOT = ['admin', 'recurring-jobs'] as const;

function invalidate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ROOT });
}

export interface RecurringListParams {
  page: number;
  activity: ActivityFilter;
}

export function useRecurringRules(params: RecurringListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<ListResponse<RecurringRuleView>>(
        `/api/admin/recurring-jobs${queryString({
          page: params.page,
          activity: params.activity,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateRecurringRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecurringRuleInput) =>
      api.post<RecurringRuleView>('/api/admin/recurring-jobs', input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateRecurringRule(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRecurringRuleInput) =>
      api.patch<RecurringRuleView>(`/api/admin/recurring-jobs/${id}`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useSetRecurringRuleActive(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<RecurringRuleView>(
        `/api/admin/recurring-jobs/${id}/${isActive ? 'resume' : 'pause'}`,
      ),
    onSuccess: () => invalidate(client),
  });
}

/**
 * Run the nightly generation by hand.
 *
 * **Safe to press twice**, which is the whole design of `generateDueJobs`, and
 * the result carries the skip count so pressing it again and seeing
 * "0 created, 14 skipped" reads as working rather than as broken. Invalidates
 * the jobs cache too — new jobs appeared, and the calendar is looking at them.
 */
export function useGenerateRecurringJobs() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<GenerationResult>('/api/admin/recurring-jobs/generate'),
    onSuccess: async () => {
      await invalidate(client);
      await client.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });
}
